import type { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';
import type { ConceptGroupsConfidence, ConceptGroupSelection } from '@verantos-dev/types-concepts';

import { useAVProgressListeners } from './useAVProgressListeners';
import { useChatWindowListeners } from './useChatWindowListeners';
import { useCohortDefinitionListeners } from './useCohortDefinitionListeners';
import { useConceptGroupConfidenceListeners } from './useConceptGroupConfidenceListeners';
import { useConceptGroupReviewListeners } from './useConceptGroupReviewListeners';
import { useConceptGroupSelectionListeners } from './useConceptGroupSelectionListeners';
import { useProgressStepListeners } from './useProgressStepListeners';
import { useResearchAssistantConnectionListeners } from './useResearchAssistantConnectionListeners';
import { useWorkflowProgressRestoreListener } from './useWorkflowProgressRestoreListener';
import { useWorkflowTimeoutGuard } from './useWorkflowTimeoutGuard';

export interface ResearchAssistantListenersProps {
  onCohortDslReceived?: (definition: CohortBuilderDefinitionLegacy) => void;
  onPartialCohortDslReceived?: (dsl: Record<string, unknown>) => void;
  onClearConceptGroupConfidence?: () => void;
  onConceptGroupConfidence?: (confidence: ConceptGroupsConfidence) => void;
  onMarkConceptGroupAsGenerated?: (conceptGroupName: string) => void;
  onConceptGroupSelections?: (selections: ConceptGroupSelection[]) => void;
  eventNameToId?: Record<string, string>;
}

export const useResearchAssistantListeners = ({
  onCohortDslReceived,
  onPartialCohortDslReceived,
  onClearConceptGroupConfidence,
  onConceptGroupConfidence,
  onMarkConceptGroupAsGenerated,
  onConceptGroupSelections,
  eventNameToId,
}: ResearchAssistantListenersProps) => {
  useResearchAssistantConnectionListeners();
  useChatWindowListeners();
  useCohortDefinitionListeners(
    onCohortDslReceived,
    onPartialCohortDslReceived,
    onClearConceptGroupConfidence
  );
  useConceptGroupConfidenceListeners(onConceptGroupConfidence, eventNameToId);
  useConceptGroupReviewListeners(onMarkConceptGroupAsGenerated);
  useConceptGroupSelectionListeners(onConceptGroupSelections);
  useProgressStepListeners();
  useAVProgressListeners();
  // Always register these listeners — the backend gates whether to emit
  // workflow_started/workflow_completed/workflow_progress_restore events.
  const { startTimer } = useWorkflowTimeoutGuard();
  useWorkflowProgressRestoreListener(startTimer);
};
